import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FactureList from '../components/facture/FactureList';
import SingleFacture from '../components/facture/SingleFacture';
import FactureJournal from '../components/facture/FactureJournal';
import UnsavedChangesGuard from '../utils/UnsavedChangesGuard';
import { useSelector } from 'react-redux';
import { Modal } from 'antd';
import './styles/devis-animations.css';

const FactureManagementPage = () => {
  const [currentView, setCurrentView] = useState('list');
  const [selectedFactureId, setSelectedFactureId] = useState(null);
  const [activeTab, setActiveTab] = useState('notValidated');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handleChangesDetected = () => setHasUnsavedChanges(true);
  const handleChangesSaved = () => setHasUnsavedChanges(false);

  const user = useSelector(state => state.user);
  const userPermissions = {
    isManager: user?.IsManager,
  };

  const handleViewChange = useCallback((newView, newTab = activeTab) => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: 'Modif. non enregistrées',
        content: 'Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter sans sauvegarder ?',
        okText: 'Quitter',
        cancelText: 'Rester',
        onOk: () => {
          setCurrentView(newView);
          if (newTab !== activeTab) {
            setActiveTab(newTab);
          }
          setHasUnsavedChanges(false);
        },
      });
    } else {
      setCurrentView(newView);
      if (newTab !== activeTab) {
        setActiveTab(newTab);
      }
    }
  }, [hasUnsavedChanges, activeTab]);

  const handleSelectFacture = (id) => {
    setSelectedFactureId(id);
    handleViewChange('viewFacture');
  };

  const handleCancelFacture = () => {
    handleViewChange('list', 'notValidated');
  };

  const handleValidate = () => {
    handleViewChange('list', 'validated');
  };

  const handleUnvalidate = () => {
    handleViewChange('list', 'notValidated');
  };

  const handleOpenJournal = () => {
    handleViewChange('journal');
  };

  const handleCloseJournal = () => {
    handleViewChange('list', activeTab);
  };

  const handleConfirmNavigation = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  // Animation variants
  const pageVariants = {
    initial: {
      opacity: 0,
      scale: 0.97,
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0],
      }
    },
    exit: {
      opacity: 0,
      scale: 0.97,
      transition: {
        duration: 0.12,
        ease: [0.25, 0.1, 0.25, 1.0],
      }
    }
  };

  return (
    <UnsavedChangesGuard
      hasUnsavedChanges={hasUnsavedChanges}
      onConfirmNavigation={handleConfirmNavigation}
    >
      <div className="devis-management-container">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentView}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageVariants}
            className="view-container"
            style={{
              width: '100%',
              height: '100%'
            }}
          >
            {currentView === 'list' && (
              <FactureList
                onSelectFacture={handleSelectFacture}
                defaultActiveTab={activeTab}
                onOpenJournal={handleOpenJournal}
              />
            )}
            {currentView === 'viewFacture' && selectedFactureId && (
              <SingleFacture
                factureId={selectedFactureId}
                onCancel={handleCancelFacture}
                onValidate={handleValidate}
                onUnvalidate={handleUnvalidate}
                userPermissions={userPermissions}
                onChangesSaved={handleChangesSaved}
                onChangesDetected={handleChangesDetected}
                onChangeView={handleViewChange}
              />
            )}
            {currentView === 'journal' && (
              <FactureJournal
                onClose={handleCloseJournal}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </UnsavedChangesGuard>
  );
};

export default FactureManagementPage;